import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../api/client'
import { Badge, Loading } from '../../components/ui'

interface AdminUser {
  id: string
  email: string
  name: string
  role: string
  walletAddress?: string
  createdAt: string
  bot?: {
    id: string
    rating: number
    jobsCompleted: number
    totalEarned: number
  }
  _count: {
    postedJobs: number
    productsListed: number
    productsPurchased: number
  }
}

interface UsersResponse {
  users: AdminUser[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [editForm, setEditForm] = useState({ name: '', role: '' })
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    fetchUsers()
  }, [search, roleFilter, page])

  const fetchUsers = async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      if (roleFilter) params.append('role', roleFilter)
      params.append('page', String(page))
      params.append('limit', '20')

      const data = await api.get<UsersResponse>(`/admin/users?${params}`)
      setUsers(data.users)
      setTotalPages(data.totalPages)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setIsLoading(false)
    }
  }

  const handleEdit = (user: AdminUser) => {
    setEditingUser(user)
    setEditForm({
      name: user.name,
      role: user.role,
    })
  }

  const handleSaveEdit = async () => {
    if (!editingUser) return

    if (!editForm.name.trim() || editForm.name.length < 2) {
      setError('Name must be at least 2 characters')
      return
    }

    setActionLoading(true)
    setError('')
    try {
      await api.patch(`/admin/users/${editingUser.id}`, editForm)
      setEditingUser(null)
      await fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async (userId: string, userName: string) => {
    if (!confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return
    }
    setActionLoading(true)
    setError('')
    try {
      await api.delete(`/admin/users/${userId}`)
      await fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user')
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/admin" className="text-secondary" style={{ fontSize: '14px' }}>Admin</Link>
          <h1>User Management</h1>
        </div>
      </div>

      {/* Filters */}
      <div className="card-static" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="input"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            style={{ flex: 1, minWidth: 200 }}
          />
          <select
            className="input select"
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1) }}
            style={{ width: 150 }}
          >
            <option value="">All Roles</option>
            <option value="USER">User</option>
            <option value="ADMIN">Admin</option>
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {isLoading ? (
        <Loading message="Loading users..." />
      ) : users.length === 0 ? (
        <div className="empty-state">
          <h3>No users found</h3>
        </div>
      ) : (
        <>
          <div className="card-static" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>User</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Role</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Wallet</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem', fontSize: '13px' }}>Jobs</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem', fontSize: '13px' }}>Products</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Created</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem', fontSize: '13px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{user.name}</div>
                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{user.email}</div>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <Badge variant={user.role === 'ADMIN' ? 'gold' : 'gray'}>{user.role}</Badge>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                        {user.walletAddress ? `${user.walletAddress.slice(0, 8)}...` : 'Not connected'}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      {user._count.postedJobs}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      {user._count.productsListed} / {user._count.productsPurchased}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '13px' }}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button className="btn btn-sm" onClick={() => handleEdit(user)} disabled={actionLoading}>
                          Edit
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ color: 'var(--error)' }}
                          onClick={() => handleDelete(user.id, user.name)}
                          disabled={actionLoading}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination" style={{ marginTop: '1rem' }}>
              <button className="pagination-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>
                &lt;
              </button>
              <span style={{ padding: '0 1rem' }}>Page {page} of {totalPages}</span>
              <button className="pagination-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>
                &gt;
              </button>
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {editingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div className="card-static" style={{ width: '100%', maxWidth: 400 }}>
            <h3 style={{ marginBottom: '1rem' }}>Edit User: {editingUser.name}</h3>
            <div className="form-group">
              <label className="label">Name</label>
              <input
                type="text"
                className="input"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label">Role</label>
              <select
                className="input select"
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
              >
                <option value="USER">User</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setEditingUser(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveEdit} disabled={actionLoading}>
                {actionLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
